// Nutrition over time, derived from archived (finished) menu days — the data
// behind the menu-history charts. Pure: takes archive records, returns series.
//
// Only ACTUAL history is charted: every figure comes from what was frozen on the
// archive record (per-serve kJ/macros, `bonusKj`, the `run` goal + tolerances).
// A value that wasn't stored is null (a gap in the chart), never substituted
// with live recipe data or current settings.

import { archivedMenuDate, formatLocalDate } from './menu.js';
import { energyThresholds } from './energy-goal.js';
import { formatEnergy } from './ingredients.js';

/** Selectable chart ranges. `months` ranges count back calendar months;
 * `aggregate` is the point granularity: daily, Monday-start weekly means, or
 * calendar-month means (longer ranges, so the chart isn't over-compressed). */
export const HISTORY_RANGES = [
  { key: 'week', label: 'Week', days: 7, aggregate: 'day' },
  { key: 'month', label: 'Month', months: 1, aggregate: 'day' },
  { key: '3m', label: '3 months', short: '3 mo', months: 3, aggregate: 'week' },
  { key: '6m', label: '6 months', short: '6 mo', months: 6, aggregate: 'week' },
  { key: 'year', label: 'Year', months: 12, aggregate: 'month' },
];

export const DEFAULT_HISTORY_RANGE = 'month';

/** The numeric fields carried by each series point (and averaged weekly). */
export const SERIES_FIELDS = [
  'kj',
  'protein',
  'fat',
  'carbs',
  'goalKj',
  'bonusKj',
  'lowerKj',
  'upperKj',
  'surplusKj',
];

const MACROS = [
  ['protein', 'perServeProtein'],
  ['fat', 'perServeFat'],
  ['carbs', 'perServeCarbs'],
];

/** Look up a range by key (falls back to the default range). */
export function historyRange(key) {
  return (
    HISTORY_RANGES.find((r) => r.key === key) ??
    HISTORY_RANGES.find((r) => r.key === DEFAULT_HISTORY_RANGE)
  );
}

/** Local-midnight Date for `date` shifted by `days` (DST-safe via setDate). */
function addDays(date, days) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * The inclusive `{ from, to }` (YYYY-MM-DD) a range covers, ending `today`.
 * A day range covers exactly `days` days; a month range starts the day AFTER
 * the same date `months` earlier (clamped to that month's length, so 31 May −
 * 3 months → 28/29 Feb), i.e. a full calendar span ending today. A range
 * aggregated by MONTH instead starts on the 1st, `months − 1` months back, so it
 * charts exactly `months` calendar months (no partial duplicate first month).
 */
export function rangeBounds(key, today = new Date()) {
  const range = historyRange(key);
  const end = addDays(today, 0);
  let start;
  if (range.months && range.aggregate === 'month') {
    start = new Date(end.getFullYear(), end.getMonth() - (range.months - 1), 1);
  } else if (range.months) {
    const y = end.getFullYear();
    const m = end.getMonth() - range.months;
    const lastDay = new Date(y, m + 1, 0).getDate();
    start = addDays(new Date(y, m, Math.min(end.getDate(), lastDay)), 1);
  } else {
    start = addDays(end, -(range.days - 1));
  }
  return { from: formatLocalDate(start), to: formatLocalDate(end) };
}

/**
 * Totals eaten in ONE archive record. `kj` is null if any entry lacks a frozen
 * `perServeKj`; each macro is null if any recipe entry lacks it (quick-energy
 * entries are energy-only and count as 0 g).
 */
export function archiveTotals(archive) {
  const totals = { kj: 0, protein: 0, fat: 0, carbs: 0 };
  for (const e of archive?.entries ?? []) {
    const servings = Number(e.servings) || 0;
    if (totals.kj !== null) {
      totals.kj = typeof e.perServeKj === 'number' ? totals.kj + e.perServeKj * servings : null;
    }
    if (e.kind === 'quick') continue;
    for (const [field, key] of MACROS) {
      if (totals[field] === null) continue;
      totals[field] = typeof e[key] === 'number' ? totals[field] + e[key] * servings : null;
    }
  }
  return totals;
}

const addNullable = (a, b) => (a === null || b === null ? null : a + b);

/**
 * One point per calendar day in `[from, to]` (inclusive, YYYY-MM-DD):
 * `{ date, archiveCount, kj, protein, fat, carbs, goalKj, bonusKj, lowerKj, upperKj,
 * surplusKj }`. A day with no archive has `archiveCount: 0` and every value null. Several records on one date are
 * summed (null if any is unknown); the bonus is the largest recorded, and the
 * goal/tolerances come from the most recently archived record that stored a
 * goal. Days without a stored goal leave the goal/threshold fields null.
 *
 * `surplusKj` is the banked surplus available that MORNING — the earliest
 * archived record's stored balance (null when never recorded). It's added to the
 * upper LIMIT (`goal + max(over %, bonus) + surplus`), since eating into the
 * surplus is allowed; an unrecorded surplus adds nothing.
 */
export function dailySeries(archives, { from, to }) {
  const byDate = new Map();
  for (const a of archives ?? []) {
    const date = archivedMenuDate(a);
    if (!date || date < from || date > to) continue;
    if (!byDate.has(date)) byDate.set(date, []);
    byDate.get(date).push(a);
  }

  const points = [];
  const [fy, fm, fd] = from.split('-').map(Number);
  for (let d = new Date(fy, fm - 1, fd); formatLocalDate(d) <= to; d = addDays(d, 1)) {
    const date = formatLocalDate(d);
    points.push(dayPoint(date, byDate.get(date) ?? []));
  }
  return points;
}

function dayPoint(date, records) {
  const point = { date, archiveCount: records.length };
  for (const f of SERIES_FIELDS) point[f] = null;
  if (records.length === 0) return point;

  let totals = null;
  let bonusKj = 0;
  for (const r of records) {
    const t = archiveTotals(r);
    totals = totals
      ? Object.fromEntries(Object.keys(t).map((k) => [k, addNullable(totals[k], t[k])]))
      : t;
    bonusKj = Math.max(bonusKj, Math.max(0, Number(r.bonusKj) || 0));
  }
  const byArchivedAt = [...records].sort((a, b) =>
    String(a.archivedAt ?? '').localeCompare(String(b.archivedAt ?? '')),
  );
  const morning = byArchivedAt.find((r) => Number.isFinite(r.surplusKj));
  const surplusKj = morning ? Math.max(0, morning.surplusKj) : null;
  Object.assign(point, totals, { bonusKj, surplusKj });

  const withGoal = byArchivedAt.filter((r) => Number.isFinite(r.run?.goalKj)).pop();
  if (withGoal) {
    const { goalKj, underTolerancePct, overTolerancePct } = withGoal.run;
    const { lower, upper } = energyThresholds(goalKj, bonusKj, underTolerancePct, overTolerancePct);
    Object.assign(point, { goalKj, lowerKj: lower, upperKj: upper + (surplusKj ?? 0) });
  }
  return point;
}

/** YYYY-MM-DD of the Monday starting the week containing `date`. */
export function weekStartOf(date) {
  const [y, m, d] = date.split('-').map(Number);
  const local = new Date(y, m - 1, d);
  return formatLocalDate(addDays(local, -((local.getDay() + 6) % 7)));
}

/** YYYY-MM-01 of the calendar month containing `date`. */
export function monthStartOf(date) {
  return `${date.slice(0, 7)}-01`;
}

/**
 * Collapse daily points into periods keyed by `periodStartOf(date)`: each field
 * is the mean of the period's NON-null days (null when none). `date` is the
 * period's start; `days` is how many of its days had any archived data.
 */
export function periodMeans(points, periodStartOf) {
  const periods = new Map();
  for (const p of points) {
    const key = periodStartOf(p.date);
    if (!periods.has(key)) periods.set(key, []);
    periods.get(key).push(p);
  }
  return [...periods].map(([date, days]) => {
    const out = { date, days: days.filter((p) => p.kj !== null || p.goalKj !== null).length };
    for (const f of SERIES_FIELDS) {
      const vals = days.map((p) => p[f]).filter((v) => v !== null);
      out[f] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    }
    return out;
  });
}

/** Monday-start weekly means (see periodMeans). */
export function weeklyMeans(points) {
  return periodMeans(points, weekStartOf);
}

/** Calendar-month means (see periodMeans). */
export function monthlyMeans(points) {
  return periodMeans(points, monthStartOf);
}

/**
 * The chart series for a range: `{ range, from, to, aggregate, points }` where
 * points are daily, weekly means or monthly means per the range's `aggregate`.
 */
export function historySeries(archives, rangeKey = DEFAULT_HISTORY_RANGE, today = new Date()) {
  const range = historyRange(rangeKey);
  const { from, to } = rangeBounds(range.key, today);
  const daily = dailySeries(archives, { from, to });
  const points =
    range.aggregate === 'week'
      ? weeklyMeans(daily)
      : range.aggregate === 'month'
        ? monthlyMeans(daily)
        : daily;
  return { range, from, to, aggregate: range.aggregate, points };
}

/** True when a series has at least one point with any archived data. */
export function hasHistoryData(series) {
  return (series?.points ?? []).some((p) => SERIES_FIELDS.some((f) => p[f] !== null));
}

const localDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Chart-ready arrays for a `historySeries` result, energy converted to the
 * display `energyUnit` (rounded, via formatEnergy) and grams rounded to 0.1:
 *   { labels, titles, macros: { protein, fat, carbs, energy },
 *     energy: { goal, bonus, eaten, upper } }
 * `labels` are short axis dates ("17 Oct", weekly "w/c 12 Oct", monthly "Oct 26");
 * `titles` are the matching tooltip headings. Nulls are kept (they render as gaps).
 */
export function historyChartData(series, energyUnit) {
  const aggregate = series?.aggregate ?? 'day';
  const points = series?.points ?? [];
  const short = { day: 'numeric', month: 'short' };
  const long = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
  const energy = (v) => formatEnergy(v, energyUnit);
  const grams = (v) => (v === null ? null : Math.round(v * 10) / 10);
  const col = (field, fn) => points.map((p) => fn(p[field]));
  const logged = (p) => `${p.days} day${p.days === 1 ? '' : 's'} logged (mean)`;
  return {
    labels: points.map((p) => {
      const d = localDate(p.date);
      if (aggregate === 'month') {
        return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
      }
      const label = d.toLocaleDateString(undefined, short);
      return aggregate === 'week' ? `w/c ${label}` : label;
    }),
    titles: points.map((p) => {
      const d = localDate(p.date);
      if (aggregate === 'month') {
        return `${d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} · ${logged(p)}`;
      }
      if (aggregate === 'week') {
        const label = d.toLocaleDateString(undefined, { ...short, year: 'numeric' });
        return `Week of ${label} · ${logged(p)}`;
      }
      return d.toLocaleDateString(undefined, long);
    }),
    macros: {
      protein: col('protein', grams),
      fat: col('fat', grams),
      carbs: col('carbs', grams),
      energy: col('kj', energy),
    },
    energy: {
      goal: col('goalKj', energy),
      bonus: col('bonusKj', energy),
      eaten: col('kj', energy),
      upper: col('upperKj', energy),
    },
  };
}
